import { isAxiosError } from "axios";
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

/**
 * HTTP 복원력(timeout · retry) 경계 (rules.md §7, latency.md).
 *
 * cross-region RTT(약 250ms)를 가정한 timeout 하한과, idempotent 요청에 한정한 exponential
 * backoff 재시도 정책을 한 곳에서 소유한다. 각 http client는 client 생성 시 이 경계의 값을
 * 가져다 쓴다.
 *
 * Owns:
 * - 각 http client의 timeout 기본값(Intake API · Overlord).
 * - 큰 바이트를 받는 직통 다운로드의 무진행 timeout.
 * - idempotent 요청의 재시도 가능 조건, 재시도 횟수, backoff 일정(지수 + jitter).
 *
 * Does not own:
 * - client 생성(baseURL · 헤더 · 자격증명)과 에러 정규화(`apiClient`/`httpError`가 소유).
 * - 시뮬레이션 지연 주입(`simulatedLatency`가 소유).
 * - polling 간격(각 화면이 소유). 재시도와 polling은 다른 메커니즘이다.
 */

// timeout 하한은 RTT(250ms)를 넉넉히 흡수한다. sub-second로 두면 정상 cross-region 연결도 실패한다.
export const API_TIMEOUT_MS = 30_000;

export type RetryPolicy = {
  maxRetries: number; // 최초 1회 외 추가 시도 수.
  baseDelayMs: number; // 첫 재시도의 기준 지연.
  maxDelayMs: number; // backoff 상한.
  idempotentMethods: ReadonlySet<string>;
};

// 250ms 왕복 위에서 즉시 여러 번 재시도하면 그만큼 지연이 쌓인다(latency.md). 추가 시도를 2회로
// 제한하고 지수 backoff + jitter로 재시도 예산을 묶는다. 최악 추가 지연 ≈ baseDelay(250~500ms) +
// 2nd(500~1000ms) + 재요청 round-trip.
const DEFAULT_RETRY_POLICY: RetryPolicy = {
  maxRetries: 2,
  baseDelayMs: 500,
  maxDelayMs: 4_000,
  idempotentMethods: new Set(["get", "head", "options"]),
};

type RetryableConfig = InternalAxiosRequestConfig & { retryCount?: number };

/**
 * client의 응답 에러 경로에 재시도를 설치한다. 정규화/401 인터셉터보다 **먼저** 등록해야
 * 원본 AxiosError(재발사에 필요한 config 포함)를 먼저 보고 재시도할 수 있다.
 */
export function installRetry(client: AxiosInstance, overrides: Partial<RetryPolicy> = {}): void {
  const policy: RetryPolicy = { ...DEFAULT_RETRY_POLICY, ...overrides };

  client.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (!isAxiosError(error) || error.config === undefined) {
        return Promise.reject(error);
      }

      const config = error.config as RetryableConfig;
      const attempt = config.retryCount ?? 0;
      if (!shouldRetry(error, config, attempt, policy)) {
        return Promise.reject(error);
      }

      config.retryCount = attempt + 1;
      await delay(backoffDelayMs(attempt + 1, policy));
      return client.request(config);
    },
  );
}

function shouldRetry(
  error: AxiosError,
  config: RetryableConfig,
  attempt: number,
  policy: RetryPolicy,
): boolean {
  if (attempt >= policy.maxRetries) {
    return false;
  }
  // 호출자가 이미 취소한 요청은 재시도하지 않는다.
  if (config.signal?.aborted === true) {
    return false;
  }
  const method = (config.method ?? "get").toLowerCase();
  if (!policy.idempotentMethods.has(method)) {
    return false;
  }
  return isRetryableError(error);
}

/** 일시적 실패만 재시도한다: timeout, 응답 없는 network, 5xx 서버 오류. 4xx는 결정적이므로 제외. */
function isRetryableError(error: AxiosError): boolean {
  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
    return true;
  }
  const status = error.response?.status;
  if (status === undefined) {
    return true; // 응답이 없으면 network 계열로 보고 재시도.
  }
  return status >= 500 && status <= 599;
}

/** 지수 backoff + equal jitter(ms). attempt는 1부터 시작하는 재시도 번호. 하한이 RTT를 흡수한다. */
function backoffDelayMs(attempt: number, policy: RetryPolicy): number {
  const exponential = policy.baseDelayMs * 2 ** (attempt - 1);
  const capped = Math.min(policy.maxDelayMs, exponential);
  const half = capped / 2;
  return Math.round(half + Math.random() * half); // [capped/2, capped]
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
